/**
 * The suppression list.
 *
 * Addresses here are blocked at enqueue, so this is the first place to look
 * when someone reports "we stopped receiving your email". Removal is offered
 * because it is occasionally right — and framed carefully, because it usually
 * is not: repeated sends to a known-bad address are what get a sending account
 * suspended.
 */

import { useState, type FormEvent } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { CopyValue } from "../components/CopyValue";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { CursorPage, Suppression, SuppressionReason } from "../api/types";

const PAGE_SIZE = 50;

/** Why an address is on the list, in the words the person reading needs. */
const REASON: Record<SuppressionReason, { label: string; detail: string }> = {
  hard_bounce: {
    label: "Hard bounce",
    detail: "The receiving server said this mailbox does not exist.",
  },
  complaint: {
    label: "Complaint",
    detail: "The recipient marked mail from you as spam.",
  },
  unsubscribe: {
    label: "Unsubscribed",
    detail: "The recipient asked to stop receiving mail.",
  },
  manual: {
    label: "Added manually",
    detail: "Someone on your team added this address.",
  },
  soft_bounce_threshold: {
    label: "Repeated soft bounces",
    detail: "Several temporary failures in a row — probably gone, but not certainly.",
  },
};

export function Suppressions() {
  const { orgId, can } = useSession();
  const [history, setHistory] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const cursor = history.at(-1) ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Suppression>>(
    (signal) =>
      api.get<CursorPage<Suppression>>("/v1/suppressions", {
        signal,
        query: { limit: PAGE_SIZE, cursor },
      }),
    [orgId, cursor],
  );

  const rows = data?.data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Suppressions</h1>
        <div className="row">
          {can("suppressions:add") && (
            <button
              type="button"
              className="oc-btn oc-btn--primary"
              onClick={() => setAdding((v) => !v)}
            >
              <Icon name="plus" size={12} />
              Suppress an address
            </button>
          )}
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <div className="content">
        {adding && can("suppressions:add") && (
          <AddSuppression
            onAdded={() => {
              setAdding(false);
              reload();
            }}
            onCancel={() => setAdding(false)}
          />
        )}

        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table suppressions-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Address</span>
              <span>Reason</span>
              <span>Suppressed</span>
              <span className="oc-table__cell--right">Action</span>
            </div>

            <div className="oc-table__body">
              {error && <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>{error.message}</p>}

              {loading && rows.length === 0 && <SkeletonRows columns={4} />}

              {!error && rows.length === 0 && !loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">Nothing suppressed</div>
                  <div className="oc-empty__hint">
                    Hard bounces, complaints and unsubscribes land here automatically and are blocked
                    before the provider is called.
                  </div>
                </div>
              )}

              {rows.map((entry) => (
                <Row
                  key={`${entry.channel}:${entry.address}`}
                  entry={entry}
                  removable={can("suppressions:remove")}
                  onRemoved={reload}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {rows.length === 0 ? "No entries" : `${rows.length} shown`}
                {data?.estimated_total != null && ` of ~${data.estimated_total.toLocaleString()}`}
              </span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!data?.has_more || !data.next_cursor}
                  onClick={() => {
                    if (data?.next_cursor) setHistory((h) => [...h, data.next_cursor!]);
                  }}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({
  entry,
  removable,
  onRemoved,
}: {
  entry: Suppression;
  removable: boolean;
  onRemoved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = REASON[entry.reason] ?? { label: entry.reason, detail: "" };

  const remove = async () => {
    const warning =
      entry.reason === "complaint"
        ? `${entry.address} marked your mail as spam. Sending to them again risks your domain's reputation with that provider. Remove the suppression anyway?`
        : `Resume sending to ${entry.address}?`;

    if (!window.confirm(warning)) return;

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/suppressions/${encodeURIComponent(entry.address)}`);
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the suppression.");
      setBusy(false);
    }
  };

  return (
    <div className="oc-table__row" role="row" style={{ cursor: "default" }}>
      <span style={{ minWidth: 0 }}>
        <CopyValue value={entry.address} className="oc-truncate" />
      </span>
      <span className="oc-body-sm" title={reason.detail}>
        {reason.label}
      </span>
      <span className="oc-mono-sm oc-text-secondary" title={entry.created_at}>
        {new Date(entry.created_at).toLocaleDateString()}
        {/* A soft-bounce suppression lapses on its own; a hard one does not.
            Saying so stops people removing an entry that would have expired. */}
        {entry.expires_at && ` · lapses ${new Date(entry.expires_at).toLocaleDateString()}`}
      </span>
      <span className="oc-table__cell--right">
        {removable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={busy}
            onClick={() => void remove()}
          >
            Remove
          </button>
        )}
        {error && <span className="field-error">{error}</span>}
      </span>
    </div>
  );
}

/**
 * Blocking an address by hand.
 *
 * Every other entry in this list was written by the delivery pipeline from what
 * a provider reported. This is the one a person creates — an abuse report, a
 * legal request, somebody asking by phone, a colleague's test inbox that must
 * never receive a campaign — and until now there was no way to make one: the
 * API had only a list and a delete, so an address could not be blocked until
 * after it had already bounced.
 */
function AddSuppression({ onAdded, onCancel }: { onAdded: () => void; onCancel: () => void }) {
  const [address, setAddress] = useState("");
  const [temporary, setTemporary] = useState(false);
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [existing, setExisting] = useState<Suppression | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setExisting(null);
    try {
      const body: Record<string, unknown> = { address: address.trim(), channel: "email" };
      if (temporary && until) body.expires_at = new Date(until).toISOString();

      // 200 means it was already suppressed and nothing changed; 201 means this
      // call created it. The distinction is worth surfacing rather than
      // swallowing — "already blocked, for a different reason" is the useful
      // answer, and it is the reason the request did not overwrite anything.
      const created = await api.post<Suppression>("/v1/suppressions", body);
      if (created.reason !== "manual") {
        setExisting(created);
        setSaving(false);
        return;
      }
      setAddress("");
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  };

  const fieldError = error instanceof ApiError ? error.fields : {};

  return (
    <form className="oc-panel" onSubmit={(event) => void submit(event)}>
      <div className="oc-panel__header">
        <div>
          <div className="oc-headline-md">Suppress an address</div>
          <div className="oc-body-sm oc-text-secondary">
            Nothing will be sent to it. This does not change the person's consent — that is what
            they chose, and this is what we know about the address.
          </div>
        </div>
      </div>

      <div className="oc-panel__body stack">
        <label className="login__field">
          <span className="oc-label">Email address</span>
          <input
            className="oc-input oc-input--sm"
            value={address}
            placeholder="someone@example.com"
            aria-invalid={Boolean(fieldError["/address"])}
            onChange={(event) => setAddress(event.target.value)}
          />
        </label>

        <label className="row" style={{ gap: "var(--oc-space-2)" }}>
          <input
            type="checkbox"
            checked={temporary}
            onChange={(event) => setTemporary(event.target.checked)}
          />
          <span className="oc-body-sm">Lift automatically on a date</span>
        </label>

        {temporary && (
          <label className="login__field" style={{ maxWidth: 240 }}>
            <span className="oc-label">Until</span>
            <input
              className="oc-input oc-input--sm"
              type="date"
              value={until}
              aria-invalid={Boolean(fieldError["/expires_at"])}
              onChange={(event) => setUntil(event.target.value)}
            />
            <span className="oc-body-sm oc-text-secondary">
              For a hold while something is investigated. Leave this off to block permanently,
              which is the ordinary case.
            </span>
          </label>
        )}

        {existing && (
          // Not an error: the address is blocked, which is what was asked for.
          // Saying which reason it carries is the point — a hard bounce is a
          // mailbox provider's verdict and worth more than a manual note, and
          // it was deliberately left in place rather than overwritten.
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            <strong>{existing.address}</strong> was already suppressed as{" "}
            <code className="oc-mono-sm">{existing.reason}</code>, so nothing changed. That record
            is kept rather than replaced — it is what tells you whether the address is safe to
            unblock later.
          </p>
        )}

        {error && <p className="field-error">{error.message}</p>}

        <div className="row" style={{ gap: "var(--oc-space-2)" }}>
          <button
            type="submit"
            className="oc-btn oc-btn--sm"
            disabled={saving || !address.trim() || (temporary && !until)}
          >
            {saving ? "Suppressing…" : "Suppress"}
          </button>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}
